/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { ACTION_VIEW, LINK_TARGET } from '@veeam/components';
import * as themes from '@veeam/components/lib/themes';
import { EActionResultStatus } from '@veeam-vspc/models/web-controllers';

import type { RefObject } from 'react';
import type React from 'react';
import type { MultiActionResult } from '@veeam-vspc/models/web-controllers';

import {
    NotificationDefaultTitles,
    NotificationMethods,
    NotificationDialogsTextKeys,
    NotificationDialogsIcons,
} from './enums';
import { TextDividerSymbols, transformTextToConfigs } from '../../helpers/text-parser';

import type { NotificationDialogsAdapter } from '../../components/NotificationDialogs';
import type { RequestSuccessResponse } from '../../interfaces';
import type { ParsedTextConfig, TextParserParams } from '../../helpers/text-parser';
import type {
    IndependentNotification,
    MultiNotificationData,
    MultiNotificationDataType,
    NotificationHideParams,
    NotificationInternalParams,
    NotificationMultiResponse,
    NotificationParams,
    NotificationResponse,
    NotificationTexts,
    NotificationDialogsButton,
    NotificationDialogsMessage,
} from './interfaces';

export class NotificationService {

    protected readonly baseButtons: NotificationDialogsButton[];
    protected readonly containerRef?: RefObject<HTMLElement>;

    static texts: NotificationTexts = {
        [NotificationDialogsTextKeys.Ok]: 'Ok',
        [NotificationDialogsTextKeys.Yes]: 'Yes',
        [NotificationDialogsTextKeys.No]: 'No',
        [NotificationDialogsTextKeys.Cancel]: 'Cancel',
        [NotificationDialogsTextKeys.Close]: 'Close',
        [NotificationDefaultTitles.Error]: 'Error',
        [NotificationDefaultTitles.Information]: 'Information',
        [NotificationDefaultTitles.Success]: 'Success',
        [NotificationDefaultTitles.Warning]: 'Warning',
    };

    protected texts: NotificationTexts = {};

    protected baseParams: NotificationInternalParams = {
        title: '',
        text: '',
        buttons: [],
        canClose: true,
        autoClose: true,
        theme: themes.blue,
    };

    protected commonNotificationAdapter: NotificationDialogsAdapter;
    protected isCommonDialogShowed = false;
    protected commonResolveCallback: ((buttonKey: NotificationDialogsTextKeys) => void) | null = null;
    protected commonRejectCallback: (() => void) | null = null;

    protected independentNotificationsStack: IndependentNotification[] = [];

    constructor(
        protected readonly NotificationAdapter: new () => NotificationDialogsAdapter,
        texts?: NotificationTexts,
        themeName?: (keyof typeof themes),
    ) {
        this.commonNotificationAdapter = new NotificationAdapter();
        this.baseParams = {
            ...this.baseParams,
            theme: (themeName && themes[themeName]) || this.baseParams.theme,
        };
        this.texts = {
            ...NotificationService.texts,
            ...texts,
        };
        this.baseButtons = [{
            type: NotificationDialogsTextKeys.Ok,
        }];
    }

    multi(title: string, resp: RequestSuccessResponse<MultiActionResult>, params: NotificationParams = {}): Promise<NotificationMultiResponse> {
        const { method, textConfigs, status } = this.parseMultiResult(resp, {
            ...params,
            internalLinkProps: {
                onClick: (event: React.MouseEvent) => {
                    const target: string | null = (event?.target as HTMLElement)?.getAttribute('target') || null;

                    if (!target || target === LINK_TARGET.self) {
                        this.hide({ independent: params.independent, asResolved: true });
                    }
                },
                ...params.internalLinkProps,
            },
        });

        return textConfigs.length === 0
            ? Promise.resolve({ status, buttonKey: NotificationDialogsTextKeys.Ok })
            : this[method](title || this.getDefaultText(this.getMethodDefaultTextKey(method)), textConfigs, params)
                .then(buttonKey => ({ buttonKey, status }));
    }

    success(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Success,
            buttons: this.getButtons(buttons, this.baseButtons),
        });
    }

    info(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Info,
            buttons: this.getButtons(buttons, this.baseButtons),
        });
    }

    error(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Error,
            buttons: this.getButtons(buttons, this.baseButtons),
        });
    }

    warning(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Warning,
            buttons: this.getButtons(buttons, this.baseButtons),
        });
    }

    alert(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Alert,
            buttons: this.getButtons(buttons, this.baseButtons),
        });
    }

    confirm(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Question,
            buttons: this.getButtons(buttons, [
                {
                    type: NotificationDialogsTextKeys.Yes,
                    text: this.getDefaultText(NotificationDialogsTextKeys.Yes),
                },
                {
                    type: NotificationDialogsTextKeys.No,
                    text: this.getDefaultText(NotificationDialogsTextKeys.No),
                    view: ACTION_VIEW.secondary,
                },
            ]),
        });
    }

    question(title: string, text: NotificationDialogsMessage, { buttons, ...restParams }: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...restParams,
            title,
            text,
            icon: NotificationDialogsIcons.Question,
            buttons: this.getButtons(buttons, [
                {
                    type: NotificationDialogsTextKeys.Yes,
                    text: this.getDefaultText(NotificationDialogsTextKeys.Yes),
                },
                {
                    type: NotificationDialogsTextKeys.No,
                    text: this.getDefaultText(NotificationDialogsTextKeys.No),
                },
                {
                    type: NotificationDialogsTextKeys.Cancel,
                    text: this.getDefaultText(NotificationDialogsTextKeys.Cancel),
                    view: ACTION_VIEW.secondary,
                },
            ]),
        });
    }

    custom(title: string, text: NotificationDialogsMessage, params: NotificationParams = {}): Promise<NotificationResponse> {
        return this.show({
            ...this.baseParams,
            ...params,
            title,
            text,
        });
    }

    hide({ independent, ...restParams }: NotificationHideParams = {}): void {
        if (independent) {
            return this.hideIndependent(restParams);
        }

        return this.hideCommon(restParams);
    }

    closeAll(): void {
        this.hideCommon({ asResolved: true });

        let counter = this.independentNotificationsStack.length ?? 0;

        while (this.independentNotificationsStack.length > 0 && counter > 0) {
            this.hideIndependent({ asResolved: true });
            counter -= 1;
        }
    }

    setTheme(themeName: keyof typeof themes): void {
        this.baseParams = {
            ...this.baseParams,
            theme: themes[themeName] || themes.blue,
        };
    }

    setTexts(texts: NotificationTexts): void {
        this.texts = {
            ...this.texts,
            ...texts,
        };
    }

    protected show(params: NotificationInternalParams): Promise<NotificationResponse> {
        if (params.independent) {
            return this.showIndependent(params);
        }

        return this.showCommon(params);
    }

    protected showCommon(params: NotificationInternalParams): Promise<NotificationResponse> {
        if (this.isCommonDialogShowed) {
            this.hideCommon({ asResolved: false }); // will reject previous dialog
        }

        const { autoClose, rejectOnFinish, text, ...restParams } = params;
        const transformParams: TextParserParams = {
            ...params,
            internalLinkProps: {
                onClick: (event: React.MouseEvent) => {
                    const target: string | null = (event?.target as HTMLElement)?.getAttribute('target') || null;

                    if (!target || target === LINK_TARGET.self) {
                        this.hide({ independent: params.independent, asResolved: true });
                    }
                },
                ...params.internalLinkProps,
            },
        };

        this.isCommonDialogShowed = true;

        return new Promise<NotificationDialogsTextKeys>((resolve, reject) => {
            this.commonResolveCallback = resolve;
            this.commonRejectCallback = reject;

            return this.commonNotificationAdapter.showDialog({
                text: typeof text === 'string' ? transformTextToConfigs(text, transformParams) : text,
                ...restParams,
                onFinish: (buttonKey: NotificationDialogsTextKeys): void => {
                    this.commonResolveCallback = null;
                    this.commonRejectCallback = null;
                    resolve(buttonKey);
                },
            }, this.containerRef);
        })
            .then((response: NotificationDialogsTextKeys) => {
                if (autoClose && this.isCommonDialogShowed) {
                    this.hideCommon();
                }

                return rejectOnFinish ? Promise.reject(response) : Promise.resolve(response);
            });
    }

    protected hideCommon({ asResolved = false }: NotificationHideParams = {}): void {
        if (!this.isCommonDialogShowed) {
            return;
        }

        if (asResolved && this.commonResolveCallback) {
            this.commonResolveCallback(NotificationDialogsTextKeys.Close); // resolve previous dialog if it overrides by new dialog
        } else if (this.commonRejectCallback) {
            this.commonRejectCallback(); // reject previous dialog if it overrides by new dialog
        }

        this.commonResolveCallback = null;
        this.commonRejectCallback = null;
        this.isCommonDialogShowed = false;
        this.commonNotificationAdapter.hideDialog();
    }

    protected showIndependent(params: NotificationInternalParams): Promise<NotificationResponse> {
        const { autoClose, rejectOnFinish, text, ...restParams } = params;
        const notificationAdapter = new this.NotificationAdapter();
        const independentNotification: IndependentNotification = {
            notificationAdapter,
            resolveCallback: null,
            rejectCallback: null,
            isAlive: true,
        };
        const transformParams: TextParserParams = {
            ...params,
            internalLinkProps: {
                onClick: (event: React.MouseEvent) => {
                    const target: string | null = (event?.target as HTMLElement)?.getAttribute('target') || null;

                    if (!target || target === LINK_TARGET.self) {
                        this.hide({ independent: params.independent, asResolved: true });
                    }
                },
                ...params.internalLinkProps,
            },
        };

        this.independentNotificationsStack.push(independentNotification);

        return new Promise<NotificationDialogsTextKeys>((resolve, reject) => {
            independentNotification.resolveCallback = resolve;
            independentNotification.rejectCallback = reject;

            return notificationAdapter.showDialog({
                text: typeof text === 'string' ? transformTextToConfigs(text, transformParams) : text,
                ...restParams,
                onFinish: (buttonKey: NotificationDialogsTextKeys): void => {
                    independentNotification.resolveCallback = null;
                    independentNotification.rejectCallback = null;
                    resolve(buttonKey);
                },
            }, this.containerRef);
        })
            .then((response: NotificationDialogsTextKeys) => {
                if (autoClose && independentNotification.isAlive) {
                    this.hideIndependent();
                }

                return rejectOnFinish ? Promise.reject(response) : Promise.resolve(response);
            });
    }

    protected hideIndependent({ asResolved = false }: NotificationHideParams = {}): void {
        if (this.independentNotificationsStack.length === 0) {
            return;
        }

        const independentNotification = this.independentNotificationsStack.pop() as IndependentNotification;
        const { resolveCallback, rejectCallback, notificationAdapter } = independentNotification;

        if (asResolved && resolveCallback) {
            resolveCallback(NotificationDialogsTextKeys.Close); // resolve previous dialog if it overrides by new dialog
        } else if (rejectCallback) {
            rejectCallback(); // reject previous dialog if it overrides by new dialog
        }

        independentNotification.isAlive = false;
        notificationAdapter.destroy();
    }

    protected getButtons(
        customButtons: NotificationDialogsButton[] | void,
        defaultButtons: NotificationDialogsButton[]
    ): NotificationDialogsButton[] {
        if (!customButtons) {
            return defaultButtons.map(btn => ({
                ...btn,
                text: this.getDefaultText(btn.type),
            }));
        }

        return customButtons.map(customButton => ({
            ...customButton,
            text: customButton.text || this.getDefaultText(customButton.type),
        }));
    }

    protected getDefaultText(key: NotificationDialogsTextKeys | NotificationDefaultTitles): string {
        return this.texts[key] as string;
    }

    protected parseMultiResult(resp: RequestSuccessResponse<MultiActionResult>, params: NotificationParams = {}): MultiNotificationData {
        const { status, message, results } = resp.data ?? {};

        if (message) {
            return {
                method: this.getMethodFromResponseType(status as EActionResultStatus),
                textConfigs: transformTextToConfigs(message, params),
                status: status as EActionResultStatus,
            };
        } else if (results && results.length > 0) {
            let currentItemStatus: EActionResultStatus = EActionResultStatus.Success;
            const texts: ParsedTextConfig[] = [];

            results.forEach(({ objectName, status: itemStatus, message: itemMessage }, index) => {
                if (itemStatus === EActionResultStatus.Success) {
                    return;
                }

                currentItemStatus = this.getStatusPriorityLevel(currentItemStatus) < this.getStatusPriorityLevel(itemStatus as EActionResultStatus)
                    ? itemStatus as EActionResultStatus
                    : currentItemStatus;

                if (objectName) {
                    texts.push({ text: `${objectName}: `, props: { asTag: 'b' } });
                    texts.push(...transformTextToConfigs(itemMessage, params));
                } else {
                    texts.push(...transformTextToConfigs(itemMessage, params));
                }

                if (index + 1 < results.length) {
                    texts.push({ type: TextDividerSymbols.Paragraph });
                }
            });

            if (texts.length > 0) {
                return {
                    method: this.getMethodFromResponseType(currentItemStatus),
                    textConfigs: texts,
                    status: currentItemStatus,
                };
            }
        }

        return {
            method: NotificationMethods.Success,
            textConfigs: [],
            status: EActionResultStatus.Success,
        };
    }

    protected getMethodFromResponseType(status: EActionResultStatus): MultiNotificationDataType {
        switch (status) {
            case (EActionResultStatus.Info):
            case (1 as any): // @TODO Remove with Ext.js
                return NotificationMethods.Info;
            case (EActionResultStatus.Warning):
            case (2 as any): // @TODO Remove with Ext.js
                return NotificationMethods.Warning;
            case (EActionResultStatus.Error):
            case (3 as any): // @TODO Remove with Ext.js
                return NotificationMethods.Error;
            default: return NotificationMethods.Success;
        }
    }

    protected getMethodDefaultTextKey(method: MultiNotificationDataType): NotificationDefaultTitles {
        switch (method) {
            case NotificationMethods.Error: return NotificationDefaultTitles.Error;
            case NotificationMethods.Info: return NotificationDefaultTitles.Information;
            case NotificationMethods.Warning: return NotificationDefaultTitles.Warning;
            default: return NotificationDefaultTitles.Success;
        }
    }

    protected getStatusPriorityLevel(status: EActionResultStatus): number {
        switch (status) {
            case (EActionResultStatus.Info):
            case (1 as any): // @TODO Remove with Ext.js
                return 1;
            case (EActionResultStatus.Warning):
            case (2 as any): // @TODO Remove with Ext.js
                return 2;
            case (EActionResultStatus.Error):
            case (3 as any): // @TODO Remove with Ext.js
                return 3;
            default: return 0;
        }
    }

}
